import React from "react";
import { toast } from "sonner";

// Green WhatsApp icon (inline SVG) — no external icon lib for this specific mark.
const WA_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M19.11 17.28c-.28-.14-1.65-.81-1.9-.9-.26-.09-.44-.14-.63.14-.18.28-.72.9-.88 1.09-.16.18-.32.21-.6.07-.28-.14-1.17-.43-2.23-1.37-.83-.74-1.38-1.65-1.55-1.94-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.49.14-.16.19-.28.28-.46.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.86-2.08-.23-.55-.46-.48-.63-.49H10c-.19 0-.49.07-.75.35-.26.28-.98.96-.98 2.34s1 2.72 1.14 2.9c.14.19 1.97 3.01 4.78 4.22.67.29 1.19.46 1.6.59.67.21 1.28.18 1.76.11.54-.08 1.65-.68 1.89-1.34.23-.65.23-1.21.16-1.34-.07-.13-.26-.21-.54-.35zM16 3.2C8.94 3.2 3.2 8.94 3.2 16c0 2.26.6 4.47 1.74 6.42L3 29l6.75-1.86A12.7 12.7 0 0 0 16 28.8C23.06 28.8 28.8 23.06 28.8 16S23.06 3.2 16 3.2zm0 23.36a10.5 10.5 0 0 1-5.35-1.47l-.38-.23-4 1.1 1.07-3.9-.25-.4A10.55 10.55 0 1 1 26.56 16c0 5.83-4.73 10.56-10.56 10.56z" />
  </svg>
);

// Instagram Threads "@" mark — simple monogram approximation.
const THREADS_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 192" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M141.537 88.988a66.667 66.667 0 0 0-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.229c8.249.053 14.474 2.452 18.503 7.129 2.932 3.405 4.892 8.111 5.864 14.05-7.32-1.243-15.235-1.626-23.702-1.14-23.86 1.375-39.198 15.29-38.168 34.629.522 9.811 5.407 18.253 13.752 23.768 7.052 4.66 16.134 6.939 25.573 6.424 12.466-.685 22.244-5.44 29.06-14.14 5.174-6.607 8.446-15.171 9.879-25.978 5.878 3.548 10.234 8.221 12.64 13.836 4.089 9.545 4.326 25.235-8.466 38.017-11.213 11.202-24.696 16.048-45.087 16.198-22.626-.168-39.732-7.42-50.845-21.55-10.404-13.229-15.78-32.35-15.978-56.827.198-24.477 5.574-43.598 15.978-56.827 11.113-14.13 28.219-21.382 50.845-21.55 22.786.169 40.19 7.454 51.741 21.646 5.662 6.96 9.934 15.71 12.755 25.913l15.518-4.156c-3.424-12.552-8.809-23.377-16.116-32.362-14.812-18.204-36.475-27.532-64.398-27.727h-.108c-27.867.195-49.28 9.56-63.634 27.831C11.397 41.048 5.10 61.628 4.884 88.51l-.001.985.001.999c.215 26.88 6.512 47.462 18.72 61.19 14.354 18.271 35.767 27.637 63.634 27.831h.108c24.771-.172 42.229-6.65 56.6-21.007 18.802-18.788 18.238-42.323 12.038-56.777-4.446-10.37-12.918-18.793-24.487-24.744ZM98.44 129.507c-10.44.588-21.286-4.098-21.82-14.135-.397-7.442 5.301-15.746 22.461-16.735 1.966-.113 3.895-.169 5.79-.169 6.235 0 12.068.606 17.373 1.765-1.978 24.702-13.58 28.713-23.804 29.274Z" />
  </svg>
);

function buildThreadsHref(title, url) {
  const text = title ? `${title}\n${url}` : url;
  return `https://www.threads.net/intent/post?text=${encodeURIComponent(text)}`;
}

// Compact icon-only Threads share (used inline, e.g. per forum reply)
export function ThreadsShareIcon({ title, url, testId = "threads-share-icon", size = "sm" }) {
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const href = buildThreadsHref(title || "", shareUrl);
  const pad = size === "sm" ? "px-2.5 py-1" : "px-3 py-1.5";
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testId}
      title="Bagikan ke Threads"
      className={`inline-flex items-center gap-1.5 ${pad} rounded-full bg-black hover:bg-[#1F2937] text-white text-xs transition-colors duration-200`}
    >
      {THREADS_ICON}
      <span>Threads</span>
    </a>
  );
}

export default function ShareButtons({ title, url, testidPrefix = "share", showCopy = true, variant = "filled" }) {
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const text = title ? `${title}` : "";
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${text}\n${shareUrl}`)}`;
  const threadsHref = buildThreadsHref(text, shareUrl);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Tautan disalin");
    } catch {
      toast.error("Gagal menyalin tautan");
    }
  };

  // Ghost variant — uniform white pills with subtle border. Brand colors only
  // appear on the icon glyph, so no button dominates the article's tail.
  if (variant === "ghost") {
    const pill = "inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#e6e9f3] text-[#4c5666] text-sm font-medium hover:border-[#cfd5e6] hover:text-[#232a36] transition-colors duration-200";
    return (
      <div className="inline-flex flex-wrap items-center gap-2" data-testid={`${testidPrefix}-buttons`}>
        <a href={waHref} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-whatsapp`} className={pill}>
          <span className="text-[#25D366]">{WA_ICON}</span> Bagikan ke WhatsApp
        </a>
        <a href={threadsHref} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-threads`} className={pill}>
          <span className="text-[#1a1a1a]">{THREADS_ICON}</span> Bagikan ke Threads
        </a>
        {showCopy && (
          <button onClick={copyLink} data-testid={`${testidPrefix}-copy`} className={pill}>
            Salin tautan
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="inline-flex flex-wrap items-center gap-2" data-testid={`${testidPrefix}-buttons`}>
      <a
        href={waHref}
        target="_blank"
        rel="noreferrer"
        data-testid={`${testidPrefix}-whatsapp`}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#1EBE58] text-white text-sm transition-colors duration-200"
      >
        {WA_ICON} Bagikan ke WhatsApp
      </a>
      <a
        href={threadsHref}
        target="_blank"
        rel="noreferrer"
        data-testid={`${testidPrefix}-threads`}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black hover:bg-[#1F2937] text-white text-sm transition-colors duration-200"
      >
        {THREADS_ICON} Bagikan ke Threads
      </a>
      {showCopy && (
        <button
          onClick={copyLink}
          data-testid={`${testidPrefix}-copy`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#EAE7E0] text-[#4B5563] text-sm hover:bg-[#F3F1ED] transition-colors duration-200"
        >
          Salin tautan
        </button>
      )}
    </div>
  );
}
